import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import { DetailsProps } from '../types';
import MeasuredTorpedo from '../../../Torpedo/MeasuredTorpedo';
import { TorpedoProps } from '../../../Torpedo/Torpedo';
import { TorpedoLayoutedItem } from '../../../Torpedo/types';
import DeviationThumbView from '../../../DeviationViews/Thumb/Standard';
import MediaService from '../../../utils/DeviationMediaService';
import { normalizeOriginalsCompletedOrders } from '../../../utils/originalsCompletedOrders';
import DefaultDetails from '../Default';
import { useNormalizedDeviationStream } from '../../../utils/hooks/useStream';
import { getOriginalsCompletedOrdersStreamId } from './saga';
import { normalizeDeviations } from '../../../redux/normalizr/helpers';
import { StreamType } from '../../../Stream';

import s from './Originals.scss';

const LOAD_MORE_LIMIT = 25;

interface CompletedOrderItem {
  id: string | number;
  width: number;
  height: number;
  deviation: PapiDeviation;
}

export const OriginalsDetails: React.FC<DetailsProps> = ({ deviation, ...props }) => {
  const { t } = useTranslation();
  const isDesktop = !useContext(MobileContext);

  const streamId = getOriginalsCompletedOrdersStreamId(deviation.deviationId);

  // Memoize the initConfig to prevent re-initialization on every render
  // Only depends on deviation.extended?.originals which is stable server data
  const initConfig = useMemo(() => {
    const normalized = normalizeOriginalsCompletedOrders(deviation.extended?.originals);
    const normalizedInitialData =
      normalized.deviations.length > 0 ? normalizeDeviations(normalized.deviations) : null;

    return {
      streamType: StreamType.WITH_OFFSET,
      autoFetch: false,
      items: normalizedInitialData?.result ?? [],
      entities: normalizedInitialData?.entities ?? {},
      itemsPerFetch: LOAD_MORE_LIMIT,
      nextOffset: normalized.nextOffset ?? undefined,
      hasMore: normalized.hasMore,
      total: normalized.total,
      streamParams: {
        deviationId: deviation.deviationId,
      },
    };
  }, [deviation.extended?.originals, deviation.deviationId]);

  const completedOrdersCount = useMemo(() => {
    return normalizeOriginalsCompletedOrders(deviation.extended?.originals).total;
  }, [deviation.extended?.originals]);

  const {
    items: allOrders,
    hasMore,
    isFetching,
    fetchNext,
  } = useNormalizedDeviationStream(streamId, 500, initConfig);

  const itemRenderer = (
    _renderProps: TorpedoProps,
    item: TorpedoLayoutedItem<CompletedOrderItem>
  ) => (
    <DeviationThumbView
      className={s['thumb']}
      deviation={item.dto.deviation}
      width={item.width}
      height={item.height}
    />
  );

  const torpedo =
    allOrders.length > 0 ? (
      <>
        <div className={s['completed-orders-heading']}>
          {t('deviation.details.originals.completedOrders', {
            count: completedOrdersCount,
          })}
        </div>
        <MeasuredTorpedo
          container={{ width: 780 }}
          items={allOrders
            .filter((item): item is PapiDeviation => Boolean(item))
            .map(item => {
              const dims = MediaService.getThumbDimensions(item);
              return {
                id: `deviation-${item.deviationId}`,
                width: dims.width || 0,
                height: dims.height || 0,
                deviation: item,
              };
            })}
          renderItem={itemRenderer}
          cookieDim="width"
          cookieType={MeasuredCookieType.DEVIATION_DESCRIPTION}
        />
        {hasMore && (
          <div className={s['show-more']}>
            <TextButton variant="main" size="small" onClick={fetchNext} disabled={isFetching}>
              {t('common.showMore')}
            </TextButton>
          </div>
        )}
      </>
    ) : null;

  return (
    <DefaultDetails
      deviation={deviation}
      {...props}
      bottomSlot={torpedo}
      showTitle={isDesktop}
      withDividerBelowStats={isDesktop}
      withGruserZone={false}
    />
  );
};
OriginalsDetails.displayName = 'OriginalsDetails';

export default OriginalsDetails;
